import { useState, useEffect } from 'react';
import { UserButton, useAuth } from '@clerk/clerk-react';
import { Search, Download, RefreshCw, Edit, Trash2, Upload, Copy, BarChart3, Code2, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import ImportLinksModal from '@/components/ImportLinksModal';
import GenerateLinksModal from '@/components/GenerateLinksModal';
import QuickGenerateModal from '@/components/QuickGenerateModal';
import EditCodeModal from '@/components/EditCodeModal';
import AnalyticsDashboard from '@/components/AnalyticsDashboard';
import { useToast } from '@/hooks/use-toast';

interface Code {
  code: string;
  privateLink: string;
  used: boolean;
  createdAt: string;
  note: string;
  usedAt?: string;
}

interface SponsoredRequest {
  email: string;
  code: string;
  language: string;
  status: string;
  createdAt: string;
  emailSentAt?: string | null;
  eventTypeId?: number;
}

const isAdminCodeGenerationEnabled = import.meta.env.VITE_ENABLE_ADMIN_CODE_GENERATION === 'true';

const AdminDashboard = () => {
  const { toast } = useToast();
  const { getToken } = useAuth();
  const [codes, setCodes] = useState<Record<string, Code>>({});
  const [sponsoredRequests, setSponsoredRequests] = useState<Record<string, SponsoredRequest>>({});
  const [filteredCodes, setFilteredCodes] = useState<Array<Code & { code: string }>>([]);
  const [filteredSponsoredRequests, setFilteredSponsoredRequests] = useState<Array<SponsoredRequest & { emailKey: string }>>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isImportLinksModalOpen, setIsImportLinksModalOpen] = useState(false);
  const [isGenerateLinksModalOpen, setIsGenerateLinksModalOpen] = useState(false);
  const [isQuickGenerateModalOpen, setIsQuickGenerateModalOpen] = useState(false);
  const [editingCode, setEditingCode] = useState<(Code & { code: string }) | null>(null);
  const [isCodesLoading, setIsCodesLoading] = useState(true);
  const [isRequestsLoading, setIsRequestsLoading] = useState(true);
  const [eventTypeId, setEventTypeId] = useState<number>(3608037); // Default to "Sponsored Private Call"

  // Fetch codes from API
  const fetchCodes = async () => {
    try {
      setIsCodesLoading(true);
      const apiUrl = import.meta.env.PROD
        ? '/api/admin/codes'
        : 'http://localhost:3000/api/admin/codes';

      const token = await getToken();
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCodes(data);
      } else {
        toast({
          title: 'Error',
          description: 'Failed to fetch codes',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error fetching codes:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch codes',
        variant: 'destructive',
      });
    } finally {
      setIsCodesLoading(false);
    }
  };

  const fetchSponsoredRequests = async () => {
    try {
      setIsRequestsLoading(true);
      const apiUrl = import.meta.env.PROD
        ? '/api/admin/sponsored-requests'
        : 'http://localhost:3000/api/admin/sponsored-requests';

      const token = await getToken();
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSponsoredRequests(data);
      } else {
        toast({
          title: 'Error',
          description: 'Failed to fetch sponsored requests',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error fetching sponsored requests:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch sponsored requests',
        variant: 'destructive',
      });
    } finally {
      setIsRequestsLoading(false);
    }
  };

  const refreshAdminData = async () => {
    await Promise.all([fetchCodes(), fetchSponsoredRequests()]);
  };

  useEffect(() => {
    refreshAdminData();
  }, []);

  // Filter and search codes
  useEffect(() => {
    let result = Object.entries(codes).map(([code, data]) => ({
      code,
      ...data,
    }));

    // Search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(c =>
        c.code.toLowerCase().includes(query) ||
        c.note.toLowerCase().includes(query) ||
        c.privateLink.toLowerCase().includes(query)
      );
    }

    setFilteredCodes(result);
  }, [codes, searchQuery]);

  useEffect(() => {
    let result = Object.entries(sponsoredRequests).map(([emailKey, data]) => ({
      emailKey,
      ...data,
    }));

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter((request) =>
        request.emailKey.toLowerCase().includes(query) ||
        request.code.toLowerCase().includes(query) ||
        request.status.toLowerCase().includes(query)
      );
    }

    result.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    setFilteredSponsoredRequests(result);
  }, [sponsoredRequests, searchQuery]);

  // Separate available and used codes
  const availableCodes = filteredCodes.filter(c => !c.used);
  const usedCodes = filteredCodes.filter(c => c.used);

  // Statistics
  const stats = {
    total: Object.keys(codes).length,
    used: Object.values(codes).filter(c => c.used).length,
    available: Object.values(codes).filter(c => !c.used).length,
    sponsoredRequests: Object.keys(sponsoredRequests).length,
  };

  // Delete code
  const handleDelete = async (code: string) => {
    if (!confirm(`Are you sure you want to delete code "${code}"?`)) return;

    try {
      const apiUrl = import.meta.env.PROD
        ? `/api/admin/codes/${code}`
        : `http://localhost:3000/api/admin/codes/${code}`;

      const token = await getToken();
      const response = await fetch(apiUrl, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Code deleted successfully',
        });
        refreshAdminData();
      } else {
        toast({
          title: 'Error',
          description: 'Failed to delete code',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error deleting code:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete code',
        variant: 'destructive',
      });
    }
  };

  // Reset code
  const handleReset = async (code: string) => {
    if (!confirm(`Reset code "${code}" to unused?`)) return;

    try {
      const apiUrl = import.meta.env.PROD
        ? `/api/admin/codes/${code}/reset`
        : `http://localhost:3000/api/admin/codes/${code}/reset`;

      const token = await getToken();
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Code reset successfully',
        });
        refreshAdminData();
      } else {
        toast({
          title: 'Error',
          description: 'Failed to reset code',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error resetting code:', error);
      toast({
        title: 'Error',
        description: 'Failed to reset code',
        variant: 'destructive',
      });
    }
  };

  // Copy code to clipboard
  const handleCopyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      toast({
        title: 'Copied!',
        description: `Code "${code}" copied to clipboard`,
      });
    } catch (error) {
      console.error('Error copying code:', error);
      toast({
        title: 'Error',
        description: 'Failed to copy code',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteSponsoredRequest = async (email: string) => {
    if (!confirm(`Delete sponsored request for "${email}"? This will also remove the associated requested code and allow a new request.`)) return;

    try {
      const encodedEmail = encodeURIComponent(email);
      const apiUrl = import.meta.env.PROD
        ? `/api/admin/sponsored-requests/${encodedEmail}`
        : `http://localhost:3000/api/admin/sponsored-requests/${encodedEmail}`;

      const token = await getToken();
      const response = await fetch(apiUrl, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Sponsored request deleted successfully',
        });
        refreshAdminData();
      } else {
        toast({
          title: 'Error',
          description: 'Failed to delete sponsored request',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error deleting sponsored request:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete sponsored request',
        variant: 'destructive',
      });
    }
  };

  // Export to CSV
  const handleExport = () => {
    const csv = [
      ['Code', 'Private Link', 'Status', 'Created', 'Used At', 'Note'].join(','),
      ...filteredCodes.map(c => [
        c.code,
        c.privateLink,
        c.used ? 'Used' : 'Available',
        new Date(c.createdAt).toLocaleDateString(),
        c.usedAt ? new Date(c.usedAt).toLocaleString() : '-',
        c.note,
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sponsor-codes-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <img
                src="/images/omsorgsterapi-logo.png"
                alt="Omsorgsterapi Logo"
                className="w-10 h-10 rounded-full"
              />
              <div>
                <h1 className="text-xl font-bold text-[#006B5F] sm:text-2xl">Admin Dashboard</h1>
                <p className="text-sm text-gray-600">Manage codes and view analytics</p>
              </div>
            </div>
            <UserButton />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="codes" className="w-full">
          <TabsList className="mb-8 flex w-full justify-start overflow-x-auto">
            <TabsTrigger value="codes" className="flex items-center gap-2">
              <Code2 className="w-4 h-4" />
              Sponsor Codes
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Analytics
            </TabsTrigger>
          </TabsList>

          <TabsContent value="codes" className="space-y-6">
            {/* Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardDescription>Total Codes</CardDescription>
                  <CardTitle className="text-3xl">{stats.total}</CardTitle>
                </CardHeader>
              </Card>
              <Card>
                <CardHeader className="pb-3">
                  <CardDescription>Used</CardDescription>
                  <CardTitle className="text-3xl text-red-600">{stats.used}</CardTitle>
                </CardHeader>
              </Card>
              <Card>
                <CardHeader className="pb-3">
                  <CardDescription>Assigned (Not Used)</CardDescription>
                  <CardTitle className="text-3xl text-green-600">{stats.available}</CardTitle>
                </CardHeader>
              </Card>
              <Card>
                <CardHeader className="pb-3">
                  <CardDescription>Sponsored Requests</CardDescription>
                  <CardTitle className="text-3xl text-[#006B5F]">{stats.sponsoredRequests}</CardTitle>
                </CardHeader>
              </Card>
            </div>

        {/* Actions Bar */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search codes and requests..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {isAdminCodeGenerationEnabled && (
                <>
                  <Button onClick={() => setIsQuickGenerateModalOpen(true)} className="bg-green-600 hover:bg-green-700">
                    <Zap className="w-4 h-4 mr-2" />
                    Quick Generate
                  </Button>
                  <Button onClick={() => setIsGenerateLinksModalOpen(true)} className="bg-[#006B5F] hover:bg-[#005A4F]">
                    <Zap className="w-4 h-4 mr-2" />
                    Bulk Generate
                  </Button>
                  <Button onClick={() => setIsImportLinksModalOpen(true)} variant="outline">
                    <Upload className="w-4 h-4 mr-2" />
                    Import Links
                  </Button>
                </>
              )}
              <Button onClick={handleExport} variant="outline">
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>
        </div>

        {/* Assigned Codes Table (Not Used Yet) */}
        <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
          <div className="bg-green-50 border-b border-green-200 px-6 py-4">
            <h2 className="text-lg font-semibold text-green-800">
              🟢 Assigned Codes ({availableCodes.length})
            </h2>
            <p className="text-sm text-green-600">Codes assigned to patients, not yet used</p>
          </div>
          <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Private Link</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Note</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isCodesLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                    Loading codes...
                  </TableCell>
                </TableRow>
              ) : availableCodes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                    No available codes
                  </TableCell>
                </TableRow>
              ) : (
                availableCodes.map((code) => (
                  <TableRow key={code.code}>
                    <TableCell className="font-mono font-semibold text-green-700">
                      <div className="flex items-center gap-2">
                        {code.code}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleCopyCode(code.code)}
                          className="h-6 w-6 p-0"
                          title="Copy code"
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-gray-600">
                      ••••••••
                    </TableCell>
                    <TableCell>{new Date(code.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell className="max-w-xs truncate text-gray-600">
                      {code.note || '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-2 justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditingCode(code)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(code.code)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </div>
        </div>

        {/* Sponsored Requests Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
          <div className="bg-[#E6F4F1] border-b border-[#CBE5E0] px-6 py-4">
            <h2 className="text-lg font-semibold text-[#006B5F]">
              Sponsored Requests ({filteredSponsoredRequests.length})
            </h2>
            <p className="text-sm text-[#2D6A61]">
              Delete a request to allow the same email address to request a new sponsored code again.
            </p>
          </div>
          <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Language</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Email Sent</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isRequestsLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    Loading sponsored requests...
                  </TableCell>
                </TableRow>
              ) : filteredSponsoredRequests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    No sponsored requests found
                  </TableCell>
                </TableRow>
              ) : (
                filteredSponsoredRequests.map((request) => (
                  <TableRow key={request.emailKey}>
                    <TableCell className="font-medium text-gray-900">
                      {request.email}
                    </TableCell>
                    <TableCell className="font-mono font-semibold text-[#006B5F]">
                      <div className="flex items-center gap-2">
                        {request.code}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleCopyCode(request.code)}
                          className="h-6 w-6 p-0"
                          title="Copy code"
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
                        {request.status}
                      </span>
                    </TableCell>
                    <TableCell className="uppercase text-gray-600">
                      {request.language}
                    </TableCell>
                    <TableCell>{new Date(request.createdAt).toLocaleString()}</TableCell>
                    <TableCell>
                      {request.emailSentAt ? new Date(request.emailSentAt).toLocaleString() : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteSponsoredRequest(request.emailKey)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </div>
        </div>

        {/* Used Codes Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-red-50 border-b border-red-200 px-6 py-4">
            <h2 className="text-lg font-semibold text-red-800">
              🔴 Used Codes ({usedCodes.length})
            </h2>
            <p className="text-sm text-red-600">Already redeemed</p>
          </div>
          <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Private Link</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Used At</TableHead>
                <TableHead>Note</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isCodesLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    Loading codes...
                  </TableCell>
                </TableRow>
              ) : usedCodes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    No used codes yet
                  </TableCell>
                </TableRow>
              ) : (
                usedCodes.map((code) => (
                  <TableRow key={code.code}>
                    <TableCell className="font-mono font-semibold text-red-700">
                      <div className="flex items-center gap-2">
                        {code.code}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleCopyCode(code.code)}
                          className="h-6 w-6 p-0"
                          title="Copy code"
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-gray-600">
                      ••••••••
                    </TableCell>
                    <TableCell>{new Date(code.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell>
                      {code.usedAt ? new Date(code.usedAt).toLocaleString() : '-'}
                    </TableCell>
                    <TableCell className="max-w-xs truncate text-gray-600">
                      {code.note || '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-2 justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReset(code.code)}
                          title="Reset to unused (Note: Cal.com link is still single-use!)"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditingCode(code)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(code.code)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </div>
        </div>
          </TabsContent>

          <TabsContent value="analytics">
            <AnalyticsDashboard />
          </TabsContent>
        </Tabs>
      </main>

      {/* Modals */}
      {isAdminCodeGenerationEnabled && (
        <>
          <QuickGenerateModal
            isOpen={isQuickGenerateModalOpen}
            onClose={() => setIsQuickGenerateModalOpen(false)}
            onSuccess={refreshAdminData}
            eventTypeId={eventTypeId}
          />
          <GenerateLinksModal
            isOpen={isGenerateLinksModalOpen}
            onClose={() => setIsGenerateLinksModalOpen(false)}
            onSuccess={refreshAdminData}
            eventTypeId={eventTypeId}
          />
          <ImportLinksModal
            isOpen={isImportLinksModalOpen}
            onClose={() => setIsImportLinksModalOpen(false)}
            onSuccess={refreshAdminData}
          />
        </>
      )}
      {editingCode && (
        <EditCodeModal
          isOpen={!!editingCode}
          onClose={() => setEditingCode(null)}
          onSuccess={refreshAdminData}
          code={editingCode}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
